/**
 * Persist CLI auth per backend API origin so localhost and cloud tokens stay separate.
 * Config file: ~/.buildautomaton/config.json
 */

import fs from 'node:fs';
import path from 'node:path';
import os from 'node:os';

export interface ServerBridgeAuth {
  workspaceId: string;
  token: string;
  refreshToken?: string;
}

export interface BridgeConfigFile {
  /** Per API origin (normalized URL without trailing slash). */
  servers?: Record<string, ServerBridgeAuth>;
  /** @deprecated single-server legacy fields */
  apiUrl?: string;
  workspaceId?: string;
  token?: string;
  refreshToken?: string;
}

function getConfigPath(): string {
  const dir = path.join(os.homedir(), '.buildautomaton');
  return path.join(dir, 'config.json');
}

export function normalizeApiUrl(url: string): string {
  return url.replace(/\/$/, '');
}

function readRawConfig(): BridgeConfigFile | null {
  try {
    const p = getConfigPath();
    const raw = fs.readFileSync(p, 'utf8');
    return JSON.parse(raw) as BridgeConfigFile;
  } catch {
    return null;
  }
}

/** Load saved auth for the given API base URL. */
export function readConfigForApi(apiUrl: string): ServerBridgeAuth | null {
  const key = normalizeApiUrl(apiUrl);
  const data = readRawConfig();
  if (!data) return null;
  const s = data.servers?.[key];
  if (s?.workspaceId && s.token) {
    return { workspaceId: s.workspaceId, token: s.token, refreshToken: s.refreshToken };
  }
  if (data.workspaceId && data.token) {
    const legacyKey = data.apiUrl ? normalizeApiUrl(data.apiUrl) : null;
    if (!legacyKey || legacyKey === key) {
      return {
        workspaceId: data.workspaceId,
        token: data.token,
        refreshToken: data.refreshToken,
      };
    }
  }
  return null;
}

export function writeConfigForApi(apiUrl: string, auth: ServerBridgeAuth): void {
  const p = getConfigPath();
  const dir = path.dirname(p);
  const key = normalizeApiUrl(apiUrl);
  const prev = readRawConfig() ?? {};
  const servers = { ...(prev.servers ?? {}), [key]: { ...auth } };
  const next: BridgeConfigFile = { ...prev, servers };
  try {
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true });
    fs.writeFileSync(p, JSON.stringify(next, null, 2), 'utf8');
  } catch (e) {
    console.error('Could not save authentication config:', e);
  }
}

/** Remove stored auth for one API origin (other servers unchanged). */
export function clearConfigForApi(apiUrl: string): void {
  const key = normalizeApiUrl(apiUrl);
  try {
    const p = getConfigPath();
    if (!fs.existsSync(p)) return;
    const prev = readRawConfig();
    if (!prev) return;
    const servers = { ...(prev.servers ?? {}) };
    delete servers[key];
    const next: BridgeConfigFile = { ...prev, servers };
    if (Object.keys(servers).length === 0 && !prev.workspaceId) {
      fs.unlinkSync(p);
      return;
    }
    if (Object.keys(servers).length === 0 && prev.workspaceId && prev.apiUrl && normalizeApiUrl(prev.apiUrl) === key) {
      const { workspaceId: _w, token: _t, refreshToken: _r, apiUrl: _a, ...rest } = prev;
      fs.writeFileSync(p, JSON.stringify(rest, null, 2), 'utf8');
      return;
    }
    fs.writeFileSync(p, JSON.stringify(next, null, 2), 'utf8');
  } catch {
    // ignore
  }
}

/** Clear all saved auth (e.g. after unrecoverable failure). */
export function clearAllConfig(): void {
  try {
    const p = getConfigPath();
    if (fs.existsSync(p)) fs.unlinkSync(p);
  } catch {
    // ignore
  }
}
